// Activity Registration Admin Component
// Admin interface for creating registration windows for winning poll activities

import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Clock, 
  Users, 
  Calendar,
  Settings,
  Save,
  X,
  AlertCircle,
  CheckCircle,
  TrendingUp,
  Timer,
  Target,
  UserPlus,
  Activity,
  Award
} from 'lucide-react';
import { useRealtime } from '../../contexts/RealtimeContext';
import { 
  createActivityRegistration,
  updateActivityRegistration,
  deleteActivityRegistration,
  getAllActivityRegistrations,
  listenToAllActivityRegistrations,
  closeActivityRegistration,
  getActivityRegistrationStats
} from '../../firebase/activityRegistrations';
import toast from 'react-hot-toast';

const ActivityRegistrationAdmin = () => {
  const { currentUser, logAdminAction } = useRealtime();
  const [registrations, setRegistrations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('registrations');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingRegistration, setEditingRegistration] = useState(null);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [registrationStats, setRegistrationStats] = useState(null);
  const [joiningLink, setJoiningLink] = useState('');
  const [showJoiningLinkModal, setShowJoiningLinkModal] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    activityType: '',
    maxParticipants: 50,
    registrationStartTime: '',
    registrationEndTime: '',
    activityDate: '',
    location: '',
    requirements: '',
    joiningDescription: '',
    isActive: true,
    pollId: '',
    winningOption: ''
  });

  // Load registrations
  useEffect(() => {
    const unsubscribe = listenToAllActivityRegistrations((result) => {
      if (result.success) {
        setRegistrations(result.data);
        setIsLoading(false);
      } else {
        setIsLoading(false);
      }
    });

    return unsubscribe;
  }, []);

  // Handle form input changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Create registration
  const handleCreateRegistration = async () => {
    if (!formData.title.trim() || !formData.description.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    if (new Date(formData.registrationStartTime) >= new Date(formData.registrationEndTime)) {
      toast.error('Registration end time must be after start time');
      return;
    }

    try {
      const registrationData = {
        ...formData,
        createdBy: currentUser.uid,
        createdAt: new Date().toISOString(),
        totalRegistrations: 0
      };

      const result = await createActivityRegistration(registrationData);
      
      if (result.success) {
        toast.success('Activity registration created successfully!');
        setShowCreateForm(false);
        resetForm();
        
        // Log admin action
        if (logAdminAction) {
          await logAdminAction('create_activity_registration', {
            registrationId: result.registrationId,
            title: registrationData.title
          });
        }
      } else {
        toast.error('Failed to create registration: ' + result.error);
      }
    } catch (error) {
      toast.error('Error creating registration: ' + error.message);
    }
  };

  // Update registration
  const handleUpdateRegistration = async () => {
    if (!editingRegistration) return;

    try {
      const updateData = {
        ...formData,
        updatedAt: new Date().toISOString()
      };

      const result = await updateActivityRegistration(editingRegistration.id, updateData);
      
      if (result.success) {
        toast.success('Registration updated successfully!');
        setEditingRegistration(null);
        resetForm();
        
        // Log admin action
        if (logAdminAction) {
          await logAdminAction('update_activity_registration', {
            registrationId: editingRegistration.id,
            title: updateData.title
          });
        }
      } else {
        toast.error('Failed to update registration: ' + result.error);
      }
    } catch (error) {
      toast.error('Error updating registration: ' + error.message);
    }
  };

  // Delete registration
  const handleDeleteRegistration = async (registrationId, title) => {
    if (window.confirm(`Are you sure you want to delete the registration "${title}"? This action cannot be undone.`)) {
      try {
        const result = await deleteActivityRegistration(registrationId);
        
        if (result.success) {
          toast.success('Registration deleted successfully!');
          
          // Log admin action
          if (logAdminAction) {
            await logAdminAction('delete_activity_registration', {
              registrationId,
              title
            });
          }
        } else {
          toast.error('Failed to delete registration: ' + result.error);
        }
      } catch (error) {
        toast.error('Error deleting registration: ' + error.message);
      }
    }
  };

  // Close registration
  const handleCloseRegistration = async (registrationId, title) => {
    try {
      const result = await closeActivityRegistration(registrationId);
      
      if (result.success) {
        toast.success('Registration closed successfully!');
        
        // Log admin action
        if (logAdminAction) {
          await logAdminAction('close_activity_registration', {
            registrationId,
            title
          });
        }
      } else {
        toast.error('Failed to close registration: ' + result.error);
      }
    } catch (error) {
      toast.error('Error closing registration: ' + error.message);
    }
  };

  // View registration stats
  const handleViewStats = async (registration) => {
    try {
      const result = await getActivityRegistrationStats(registration.id);
      
      if (result.success) {
        setRegistrationStats(result.data);
        setSelectedRegistration(registration);
        setActiveTab('stats');
      } else {
        toast.error('Failed to load registration stats: ' + result.error);
      }
    } catch (error) {
      toast.error('Error loading registration stats: ' + error.message);
    }
  };

  // Generate joining link
  const generateJoiningLink = (registration) => {
    const baseUrl = window.location.origin;
    const joiningLink = `${baseUrl}/join-activity/${registration.id}`;
    setJoiningLink(joiningLink);
    setShowJoiningLinkModal(true);
  };

  // Copy joining link to clipboard
  const copyJoiningLink = () => {
    navigator.clipboard.writeText(joiningLink).then(() => {
      toast.success('Joining link copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  // Clean up registration (delete everything)
  const handleCleanup = async (registration) => {
    if (!window.confirm('Are you sure you want to delete this registration and all associated data? This action cannot be undone.')) {
      return;
    }

    try {
      const result = await deleteActivityRegistration(registration.id);
      if (result.success) {
        toast.success('Registration and all associated data deleted successfully');
        setSelectedRegistration(null);
        setActiveTab('registrations');
      } else {
        toast.error('Failed to delete registration: ' + result.error);
      }
    } catch (error) {
      toast.error('Error deleting registration: ' + error.message);
    }
  };

  // Edit registration
  const handleEditRegistration = (registration) => {
    setEditingRegistration(registration);
    setFormData({
      title: registration.title,
      description: registration.description,
      activityType: registration.activityType,
      maxParticipants: registration.maxParticipants,
      registrationStartTime: registration.registrationStartTime,
      registrationEndTime: registration.registrationEndTime,
      activityDate: registration.activityDate,
      location: registration.location,
      requirements: registration.requirements,
      joiningDescription: registration.joiningDescription || '',
      isActive: registration.isActive,
      pollId: registration.pollId || '',
      winningOption: registration.winningOption || ''
    });
    setShowCreateForm(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      activityType: '',
      maxParticipants: 50,
      registrationStartTime: '',
      registrationEndTime: '',
      activityDate: '',
      location: '',
      requirements: '',
      joiningDescription: '',
      isActive: true,
      pollId: '',
      winningOption: ''
    });
    setEditingRegistration(null);
  };

  // Format date
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  // Check if registration is open
  const isRegistrationOpen = (registration) => {
    const now = new Date();
    const startTime = new Date(registration.registrationStartTime);
    const endTime = new Date(registration.registrationEndTime);
    return registration.isActive && now >= startTime && now <= endTime;
  };

  // Check if registration is expired
  const isRegistrationExpired = (registration) => {
    const now = new Date();
    const endTime = new Date(registration.registrationEndTime);
    return now > endTime;
  };

  // Get registration status
  const getRegistrationStatus = (registration) => {
    if (!registration.isActive) return { text: 'Closed', color: 'red' };
    if (isRegistrationExpired(registration)) return { text: 'Expired', color: 'orange' };
    if (isRegistrationOpen(registration)) return { text: 'Open', color: 'green' };
    return { text: 'Upcoming', color: 'blue' };
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
        <span className="ml-3 text-gray-600 dark:text-gray-400">Loading registrations...</span>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center">
                <UserPlus className="mr-3 text-green-600" size={32} />
                Activity Registration Management
              </h1>
              <p className="text-gray-600 dark:text-gray-400 mt-2">
                Create registration windows for winning poll activities
              </p>
            </div>
            <button
              onClick={() => setShowCreateForm(true)}
              className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Registration
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mb-8">
          <div className="border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8 px-6">
              {[
                { id: 'registrations', label: 'All Registrations', icon: Activity },
                { id: 'stats', label: 'Statistics', icon: TrendingUp }
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`py-4 px-1 border-b-2 font-medium text-sm flex items-center ${
                    activeTab === tab.id
                      ? 'border-green-500 text-green-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <tab.icon className="h-4 w-4 mr-2" />
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>

          <div className="p-6">
            {activeTab === 'registrations' && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    All Registrations ({registrations.length})
                  </h3>
                </div>

                {registrations.length === 0 ? (
                  <div className="text-center py-12">
                    <UserPlus className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
                      No registrations found
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">
                      Create your first activity registration to get started
                    </p>
                    <button
                      onClick={() => setShowCreateForm(true)}
                      className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      Create Registration
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {registrations.map((registration) => {
                      const status = getRegistrationStatus(registration);
                      return (
                        <motion.div
                          key={registration.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="border border-gray-200 dark:border-gray-700 rounded-lg p-6 hover:shadow-md transition-shadow"
                        >
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex-1">
                              <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                                {registration.title}
                              </h4>
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                                {registration.description}
                              </p>
                              <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-gray-400">
                                <div className="flex items-center">
                                  <Users className="h-4 w-4 mr-1" />
                                  <span>{registration.totalRegistrations || 0}/{registration.maxParticipants} participants</span>
                                </div>
                                <div className="flex items-center">
                                  <Calendar className="h-4 w-4 mr-1" />
                                  <span>{formatDate(registration.createdAt)}</span>
                                </div>
                              </div>
                            </div>
                            
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              status.color === 'green' 
                                ? 'bg-green-100 text-green-600 dark:bg-green-900 dark:text-green-300'
                                : status.color === 'orange'
                                ? 'bg-orange-100 text-orange-600 dark:bg-orange-900 dark:text-orange-300'
                                : status.color === 'blue'
                                ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300'
                                : 'bg-red-100 text-red-600 dark:bg-red-900 dark:text-red-300'
                            }`}>
                              {status.text}
                            </span>
                          </div>

                          <div className="space-y-2 mb-4">
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              <strong>Activity:</strong> {registration.activityType}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              <strong>Date:</strong> {formatDate(registration.activityDate)}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              <strong>Location:</strong> {registration.location}
                            </div>
                            <div className="text-sm text-gray-600 dark:text-gray-400">
                              <strong>Registration Period:</strong> {formatDate(registration.registrationStartTime)} - {formatDate(registration.registrationEndTime)}
                            </div>
                          </div>

                          <div className="flex items-center space-x-2">
                            <button
                              onClick={() => handleViewStats(registration)}
                              className="flex items-center px-3 py-1 text-sm bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Stats
                            </button>
                            
                            <button
                              onClick={() => generateJoiningLink(registration)}
                              className="flex items-center px-3 py-1 text-sm bg-green-100 text-green-600 rounded-lg hover:bg-green-200"
                            >
                              <UserPlus className="h-4 w-4 mr-1" />
                              Join Link
                            </button>
                            
                            <button
                              onClick={() => handleEditRegistration(registration)}
                              className="flex items-center px-3 py-1 text-sm bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200"
                            >
                              <Edit className="h-4 w-4 mr-1" />
                              Edit
                            </button>
                            
                            <button
                              onClick={() => handleCleanup(registration)}
                              className="flex items-center px-3 py-1 text-sm bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Cleanup
                            </button>
                            
                            {registration.isActive && !isRegistrationExpired(registration) && (
                              <button
                                onClick={() => handleCloseRegistration(registration.id, registration.title)}
                                className="flex items-center px-3 py-1 text-sm bg-orange-100 text-orange-600 rounded-lg hover:bg-orange-200"
                              >
                                <X className="h-4 w-4 mr-1" />
                                Close
                              </button>
                            )}
                            
                            <button
                              onClick={() => handleDeleteRegistration(registration.id, registration.title)}
                              className="flex items-center px-3 py-1 text-sm bg-red-100 text-red-600 rounded-lg hover:bg-red-200"
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Delete
                            </button>
                          </div>
                        </motion.div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {activeTab === 'stats' && selectedRegistration && registrationStats && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      Registration Statistics: {selectedRegistration.title}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {registrationStats.totalRegistrations} total registrations
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <div className="flex items-center">
                      <Users className="h-8 w-8 text-blue-600 mr-3" />
                      <div>
                        <p className="text-sm text-blue-600 dark:text-blue-400">Total Registrations</p>
                        <p className="text-2xl font-bold text-blue-900 dark:text-blue-100">
                          {registrationStats.totalRegistrations}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 dark:bg-green-900/20 rounded-lg p-4">
                    <div className="flex items-center">
                      <Award className="h-8 w-8 text-green-600 mr-3" />
                      <div>
                        <p className="text-sm text-green-600 dark:text-green-400">Capacity</p>
                        <p className="text-2xl font-bold text-green-900 dark:text-green-100">
                          {selectedRegistration.maxParticipants}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-purple-50 dark:bg-purple-900/20 rounded-lg p-4">
                    <div className="flex items-center">
                      <TrendingUp className="h-8 w-8 text-purple-600 mr-3" />
                      <div>
                        <p className="text-sm text-purple-600 dark:text-purple-400">Fill Rate</p>
                        <p className="text-2xl font-bold text-purple-900 dark:text-purple-100">
                          {Math.round((registrationStats.totalRegistrations / selectedRegistration.maxParticipants) * 100)}%
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Participants List with Contact Details */}
                {registrationStats.participants && registrationStats.participants.length > 0 && (
                  <div className="mt-8">
                    <h4 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Registered Participants
                    </h4>
                    <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                      <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
                          <thead className="bg-gray-50 dark:bg-gray-700">
                            <tr>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Participant
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Email
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Mobile
                              </th>
                              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                                Registered At
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                            {registrationStats.participants.map((participant) => (
                              <tr key={participant.id}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="flex items-center">
                                    <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-blue-500 rounded-full flex items-center justify-center text-white text-sm font-medium mr-3">
                                      {participant.userData?.name?.charAt(0) || 'U'}
                                    </div>
                                    <div>
                                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                                        {participant.userData?.name || 'Anonymous'}
                                      </div>
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900 dark:text-white">
                                    {participant.userData?.email || 'No email'}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-900 dark:text-white">
                                    {participant.userData?.mobile || 'No mobile'}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-500 dark:text-gray-400">
                                    {new Date(participant.registeredAt?.toDate?.() || participant.registeredAt).toLocaleString()}
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create/Edit Registration Modal */}
      <AnimatePresence>
        {showCreateForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    {editingRegistration ? 'Edit Registration' : 'Create Activity Registration'}
                  </h2>
                  <button
                    onClick={() => {
                      setShowCreateForm(false);
                      resetForm();
                    }}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-6">
                  {/* Basic Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Title *
                      </label>
                      <input
                        type="text"
                        value={formData.title}
                        onChange={(e) => handleInputChange('title', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                        placeholder="Enter activity title..."
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Activity Type *
                      </label>
                      <select
                        value={formData.activityType}
                        onChange={(e) => handleInputChange('activityType', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      >
                        <option value="">Select activity type</option>
                        <option value="workshop">Workshop</option>
                        <option value="seminar">Seminar</option>
                        <option value="meetup">Meetup</option>
                        <option value="competition">Competition</option>
                        <option value="training">Training</option>
                        <option value="event">Event</option>
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Description *
                    </label>
                    <textarea
                      value={formData.description}
                      onChange={(e) => handleInputChange('description', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Enter activity description..."
                    />
                  </div>

                  {/* Registration Period */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Registration Start Time *
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.registrationStartTime}
                        onChange={(e) => handleInputChange('registrationStartTime', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Registration End Time *
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.registrationEndTime}
                        onChange={(e) => handleInputChange('registrationEndTime', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>

                  {/* Activity Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Activity Date *
                      </label>
                      <input
                        type="datetime-local"
                        value={formData.activityDate}
                        onChange={(e) => handleInputChange('activityDate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Max Participants *
                      </label>
                      <input
                        type="number"
                        min="1"
                        value={formData.maxParticipants}
                        onChange={(e) => handleInputChange('maxParticipants', parseInt(e.target.value))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Location *
                    </label>
                    <input
                      type="text"
                      value={formData.location}
                      onChange={(e) => handleInputChange('location', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Enter activity location..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Requirements
                    </label>
                    <textarea
                      value={formData.requirements}
                      onChange={(e) => handleInputChange('requirements', e.target.value)}
                      rows={2}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Enter any requirements or prerequisites..."
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Joining Instructions for Participants
                    </label>
                    <textarea
                      value={formData.joiningDescription}
                      onChange={(e) => handleInputChange('joiningDescription', e.target.value)}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Enter instructions for participants who have registered (e.g., meeting link, preparation steps, what to bring, etc.)..."
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      This information will be shown to registered participants when they access the joining link
                    </p>
                  </div>

                  {/* Registration Form Information */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-blue-800 dark:text-blue-200 mb-2">
                      Registration Form Fields
                    </h4>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mb-2">
                      Users will be required to provide the following information when registering:
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
                      <div className="flex items-center text-blue-700 dark:text-blue-300">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                        Full Name (Required)
                      </div>
                      <div className="flex items-center text-blue-700 dark:text-blue-300">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                        Email Address (Required)
                      </div>
                      <div className="flex items-center text-blue-700 dark:text-blue-300">
                        <div className="w-2 h-2 bg-blue-500 rounded-full mr-2"></div>
                        Mobile Number (Required)
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={formData.isActive}
                      onChange={(e) => handleInputChange('isActive', e.target.checked)}
                      className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                    />
                    <label htmlFor="isActive" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                      Registration is active
                    </label>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end space-x-3 mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => {
                      setShowCreateForm(false);
                      resetForm();
                    }}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={editingRegistration ? handleUpdateRegistration : handleCreateRegistration}
                    className="flex items-center px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    {editingRegistration ? 'Update Registration' : 'Create Registration'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Joining Link Modal */}
      <AnimatePresence>
        {showJoiningLinkModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full"
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Joining Link Generated
                  </h2>
                  <button
                    onClick={() => setShowJoiningLinkModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Joining Link
                    </label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={joiningLink}
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                      />
                      <button
                        onClick={copyJoiningLink}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm"
                      >
                        Copy
                      </button>
                    </div>
                  </div>

                  <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                    <div className="flex items-start">
                      <AlertCircle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mr-2 mt-0.5" />
                      <div className="text-sm text-yellow-800 dark:text-yellow-200">
                        <p className="font-medium mb-1">Important:</p>
                        <ul className="list-disc list-inside space-y-1">
                          <li>Only registered participants can access this link</li>
                          <li>Users must be logged in with their registered email</li>
                          <li>Share this link only with confirmed participants</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                  <button
                    onClick={() => setShowJoiningLinkModal(false)}
                    className="px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                  >
                    Close
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ActivityRegistrationAdmin;
